import { Box } from '@mui/material';
import { Field, FieldProps, useFormikContext } from 'formik';
import { FormHelperText, Grid, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { PoolTextArea, PoolTextField, PoolTypography } from '@synergy/components';
import { useEffect, useRef } from 'react';

import { AddressFormValues } from '../../models/address-form-values';
import { CountryAutocomplete, CountryAutocompleteList } from '../country/country-autocomplete-list';
import { RegionAutocompleteList } from '../country/region-autocomplete-list';
import { RegionWithId } from '../../../../common/models/region';
import { bestLocaleSelector } from '../../../../common/selectors/locale-selectors';
import { getCountries } from '../../utils/get-country-values';
import { getRegionsByCountry, resetRegions } from '../../../../common/actions/region-actions';
import { regionFromZip } from '../../utils/region-from-zip';
import { regionsSelector } from '../../../../common/selectors/regions-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

interface AddressMainDataProps {
    requireRegion: boolean;
}

export const AddressMainData = (props: AddressMainDataProps) => {
    const locale = useAppSelector(state => bestLocaleSelector(state));
    const formik = useFormikContext<AddressFormValues>();
    const dispatch = useThunkDispatch();
    const regionsByCountry = useAppSelector(regionsSelector);

    useEffect(() => {
        if (formik.values.country) {
            dispatch(getRegionsByCountry({ countryCode: formik.values.country.code }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCountrySelected = async (country: CountryAutocomplete | null) => {
        const values = { ...formik.values };
        values.country = country ?? undefined;
        values.region = undefined;
        if (country) {
            dispatch(getRegionsByCountry({ countryCode: country.code }));
        } else {
            dispatch(resetRegions());
        }
        await formik.setValues(values);
    };

    const regions =
        (formik.values?.country?.code &&
            (regionsByCountry?.[formik.values.country.code] ?? []).map((region, index) => ({
                id: index + 1,
                code: region.code,
                name: region.name,
            }))) ||
        [];

    // The region was a required field the form gave no way to arrive at: the
    // postcode already says it, so the form says it too (#2212, R-31). The
    // list stays open for the few places the postcode ranges get wrong.
    const { setFieldValue, values } = formik;
    const zip = values.zip;
    const countryCode = values.country?.code;
    const hasRegion = !!values.region;
    // What was last looked at, so the suggestion is made once per postcode.
    // Without this, clearing the region by hand would put it straight back.
    const suggestedFor = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (0 === regions.length) {
            return;
        }

        const key = `${countryCode ?? ''}:${zip ?? ''}`;

        if (key === suggestedFor.current) {
            return;
        }

        suggestedFor.current = key;

        if (hasRegion) {
            return;
        }

        const suggestion = regionFromZip(zip, countryCode, regions);

        if (suggestion) {
            setFieldValue('region', suggestion);
        }
        // `regions` is rebuilt on every render; its content is covered by the
        // country it was fetched for.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zip, countryCode, hasRegion, regions.length, setFieldValue]);

    return (
        <Grid container={true} item={true} spacing={2}>
            <Grid item={true} xs={12}>
                <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.detail.addressData" />
            </Grid>

            <Grid item={true} xs={12}>
                <Field name="street">
                    {({ field, meta }: FieldProps<string>) => (
                        <PoolTextField
                            fullWidth={true}
                            required={true}
                            label={<FormattedMessage id="app.label.street" />}
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            {...field}
                        />
                    )}
                </Field>
            </Grid>
            <Grid item={true} xs={6}>
                <Field name="number">
                    {({ field, meta }: FieldProps<string>) => (
                        <PoolTextField
                            fullWidth={true}
                            required={false}
                            label={<FormattedMessage id="app.label.number" />}
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            {...field}
                        />
                    )}
                </Field>
            </Grid>
            <Grid item={true} xs={6}>
                <Field name="addition">
                    {({ field, meta }: FieldProps<string>) => (
                        <PoolTextField
                            fullWidth={true}
                            label={<FormattedMessage id="app.label.addressAdditon" />}
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            {...field}
                        />
                    )}
                </Field>
            </Grid>
            <Grid item={true} xs={3}>
                <Field name="zip">
                    {({ field, meta }: FieldProps<string>) => (
                        <PoolTextField
                            fullWidth={true}
                            required={true}
                            label={<FormattedMessage id="app.label.zip" />}
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            {...field}
                        />
                    )}
                </Field>
            </Grid>
            <Grid item={true} xs={9}>
                <Field name="city">
                    {({ field, meta }: FieldProps<string>) => (
                        <PoolTextField
                            fullWidth={true}
                            required={true}
                            label={<FormattedMessage id="app.label.city" />}
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            {...field}
                        />
                    )}
                </Field>
            </Grid>
            <Grid item={true} xs={6}>
                <Field name="country">
                    {({ field, meta }: FieldProps<CountryAutocomplete>) => (
                        <>
                            <CountryAutocompleteList
                                {...field}
                                items={getCountries(locale)}
                                onSelected={selected => handleCountrySelected(selected)}
                                error={!!meta.touched && !!meta.error}
                                value={field.value}
                                disablePreventOverflow={true}
                                required={true}
                            />
                            {meta.error && meta.touched && (
                                <FormHelperText error={true}>
                                    <FormattedMessage id={meta.error} />
                                </FormHelperText>
                            )}
                        </>
                    )}
                </Field>
            </Grid>
            <Grid item={true} xs={6}>
                <Field name="region">
                    {({ field, form, meta }: FieldProps<RegionWithId>) => (
                        <>
                            <Tooltip
                                title={
                                    !form.values.country ? (
                                        <FormattedMessage id="app.contactsAndOrganizations.detail.regionDisabledText" />
                                    ) : (
                                        ''
                                    )
                                }
                                placement="top"
                            >
                                <Box>
                                    <RegionAutocompleteList
                                        {...field}
                                        items={regions}
                                        onSelected={selected => {
                                            form.setFieldValue(field.name, selected);
                                        }}
                                        error={!!meta.touched && !!meta.error}
                                        value={field.value}
                                        disablePreventOverflow={true}
                                        required={props.requireRegion}
                                        disabled={!form.values.country}
                                    />
                                </Box>
                            </Tooltip>
                            {meta.error && meta.touched && (
                                <FormHelperText error={true}>
                                    <FormattedMessage id={meta.error} />
                                </FormHelperText>
                            )}
                        </>
                    )}
                </Field>
            </Grid>
            <Grid item={true} xs={12}>
                <Field name="note">
                    {({ field, meta }: FieldProps<string>) => (
                        <PoolTextArea
                            fullWidth={true}
                            label={<FormattedMessage id="app.contactsAndOrganizations.detail.internalNotes" />}
                            rows="5"
                            rowsMax="50"
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            {...field}
                        />
                    )}
                </Field>
            </Grid>
        </Grid>
    );
};
